import { useState, useRef, useCallback, useEffect } from 'react';
import type { Language, Locale } from '@/config/locales';
import { toast } from 'sonner@2.0.3';
import { profileTranslations } from '../components/profile/profileTranslations';
import { useAuth } from '@/contexts/AuthContext';
import { userApi } from '@/lib/api/users';
import { ApiClientError } from '@/lib/api/client';
import type { Gender } from '@/lib/api/types';
import { logger } from '@/utils/logger';

export interface ProfileData {
  name: string;
  phone: string;
  email: string;
  birthday: string;
  gender: Gender | '';
}

export interface UseProfileStateReturn {
  isEditing: boolean;
  profileData: ProfileData;
  originalProfileData: ProfileData;
  profileImage: string | null;
  originalProfileImage: string | null;
  showAvatarModal: boolean;
  showCalendar: boolean;
  showBirthdayConfirm: boolean;
  tempBirthday: string | null;
  isBirthdayLocked: boolean;
  showUnsavedChangesDialog: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isSaving: boolean;
  saveError: string | null;

  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileData>>;
  setProfileImage: React.Dispatch<React.SetStateAction<string | null>>;
  setShowAvatarModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCalendar: React.Dispatch<React.SetStateAction<boolean>>;
  setShowBirthdayConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  setTempBirthday: React.Dispatch<React.SetStateAction<string | null>>;
  setIsBirthdayLocked: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUnsavedChangesDialog: React.Dispatch<React.SetStateAction<boolean>>;

  hasUnsavedChanges: () => boolean;
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAvatarSelect: (avatarUrl: string) => void;
  handleImageClick: () => void;
  handleSave: (onBack: () => void, language: Locale) => Promise<void>;
  handleBackWithCheck: (onBack: () => void) => void;
  handleSaveAndExit: (onBack: () => void, language: Locale) => Promise<void>;
  handleDiscardChanges: (onBack: () => void) => void;
  handleBirthdayConfirm: () => void;
  handleBirthdayCancel: () => void;
  clearSaveError: () => void;
}

const SUCCESS_TOAST_STYLE = {
  background: '#7A4FFC',
  color: '#fff',
  fontWeight: '600',
  fontSize: '14px',
  padding: '12px 20px',
  borderRadius: '5px',
  boxShadow: '0 4px 12px rgba(122, 79, 252, 0.25)',
} as const;

// #522 — Locale 단일 출처 정합 (15언어 전수). 누락 시 컴파일 에러.
type LocalizedMessage =
  & Record<Language, string>
  & Record<Exclude<Locale, Language>, string>;

const ERROR_MESSAGES: Record<'default' | 'auth' | 'network', LocalizedMessage> = {
  default: {
    ko: '프로필 저장에 실패했습니다.',
    es: 'Error al guardar el perfil.',
    en: 'Failed to save profile.',
    pt: 'Falha ao salvar o perfil.',
    fr: "Échec de l'enregistrement du profil.",
    zh: '保存个人资料失败。',
    ja: 'プロフィールの保存に失敗しました。',
    hi: 'प्रोफ़ाइल सहेजने में विफल।',
    de: 'Profil konnte nicht gespeichert werden.',
    it: 'Impossibile salvare il profilo.',
    ru: 'Не удалось сохранить профиль.',
    ar: 'فشل حفظ الملف الشخصي.',
    vi: 'Lưu hồ sơ thất bại.',
    th: 'บันทึกโปรไฟล์ไม่สำเร็จ',
    nl: 'Profiel opslaan mislukt.',
  },
  auth: {
    ko: '로그인이 만료되었습니다. 다시 로그인해주세요.',
    es: 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
    en: 'Your session has expired. Please log in again.',
    pt: 'Sua sessão expirou. Faça login novamente.',
    fr: 'Votre session a expiré. Veuillez vous reconnecter.',
    zh: '您的登录已过期，请重新登录。',
    ja: 'ログインの有効期限が切れました。再度ログインしてください。',
    hi: 'आपका सत्र समाप्त हो गया है। कृपया फिर से लॉगिन करें।',
    de: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    it: 'La sessione è scaduta. Effettua nuovamente il login.',
    ru: 'Сеанс истёк. Пожалуйста, войдите снова.',
    ar: 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.',
    vi: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.',
    th: 'เซสชันหมดอายุแล้ว กรุณาเข้าสู่ระบบอีกครั้ง',
    nl: 'Uw sessie is verlopen. Log opnieuw in.',
  },
  network: {
    ko: '네트워크 연결을 확인해주세요.',
    es: 'Verifica tu conexión a internet.',
    en: 'Please check your network connection.',
    pt: 'Verifique sua conexão com a internet.',
    fr: 'Veuillez vérifier votre connexion réseau.',
    zh: '请检查您的网络连接。',
    ja: 'ネットワーク接続を確認してください。',
    hi: 'कृपया अपना नेटवर्क कनेक्शन जांचें।',
    de: 'Bitte überprüfen Sie Ihre Netzwerkverbindung.',
    it: 'Controlla la connessione di rete.',
    ru: 'Проверьте подключение к сети.',
    ar: 'يرجى التحقق من اتصال الشبكة.',
    vi: 'Vui lòng kiểm tra kết nối mạng.',
    th: 'กรุณาตรวจสอบการเชื่อมต่อเครือข่าย',
    nl: 'Controleer uw netwerkverbinding.',
  },
} as const;

const emptyProfileData: ProfileData = {
  name: '',
  phone: '',
  email: '',
  birthday: '',
  gender: '',
};

export function useProfileState(): UseProfileStateReturn {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(true);
  const [initializedUserId, setInitializedUserId] = useState<number | null>(null);

  const [originalProfileData, setOriginalProfileData] = useState<ProfileData>(emptyProfileData);
  const [profileData, setProfileData] = useState<ProfileData>(emptyProfileData);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    // 로그아웃 시 프로필 데이터 초기화
    if (!user) {
      setOriginalProfileData(emptyProfileData);
      setProfileData(emptyProfileData);
      setInitializedUserId(null);
      return;
    }

    // 이미 초기화된 사용자인 경우 스킵
    if (user.id === initializedUserId) return;

    // birthday + gender BE 활성화 (admin#1060 Phase 1 머지 완료 — menu#275)
    const userData: ProfileData = {
      name: user.name ?? '',
      phone: user.phone_number ?? '',
      email: user.email ?? '',
      birthday: user.birthday ?? '',
      gender: user.gender ?? '',
    };
    setOriginalProfileData(userData);
    setProfileData(userData);
    setIsBirthdayLocked(user.birthday !== null && user.birthday !== '');
    setInitializedUserId(user.id);
  }, [user, initializedUserId]);

  const [originalProfileImage, setOriginalProfileImage] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showBirthdayConfirm, setShowBirthdayConfirm] = useState(false);
  const [tempBirthday, setTempBirthday] = useState<string | null>(null);
  const [isBirthdayLocked, setIsBirthdayLocked] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const hasUnsavedChanges = useCallback(() => {
    // phone/email은 read-only. birthday + gender 는 admin#1060 머지 후 활성화 (menu#275).
    return (
      profileData.name !== originalProfileData.name ||
      profileData.birthday !== originalProfileData.birthday ||
      profileData.gender !== originalProfileData.gender ||
      profileImage !== originalProfileImage
    );
  }, [
    profileData.name,
    profileData.birthday,
    profileData.gender,
    originalProfileData.name,
    originalProfileData.birthday,
    originalProfileData.gender,
    profileImage,
    originalProfileImage,
  ]);

  const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      logger.warn('[Profile] File size exceeds limit:', file.size);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setProfileImage(reader.result as string);
    reader.onerror = () => logger.error('[Profile] Failed to read file:', reader.error);
    reader.readAsDataURL(file);
  }, []);

  const handleAvatarSelect = useCallback((avatarUrl: string) => {
    setProfileImage(avatarUrl);
    setShowAvatarModal(false);
  }, []);

  const handleImageClick = useCallback(() => {
    setShowAvatarModal(true);
  }, []);

  const clearSaveError = useCallback(() => setSaveError(null), []);

  const handleSave = useCallback(async (onBack: () => void, language: Locale): Promise<void> => {
    if (isSaving) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      // admin#1060 Phase 1 머지 완료 — birthday + gender 전송 활성화 (menu#275).
      // 빈 문자열 ('') 은 명시적 null 로 변환 (clearing 의도). undefined 는 'sometimes' validation 에서 누락 시 무시.
      const response = await userApi.updateProfile({
        name: profileData.name.trim(),
        phone_number: profileData.phone || undefined,
        avatar_url: profileImage || undefined,
        birthday: profileData.birthday === '' ? null : profileData.birthday,
        gender: profileData.gender === '' ? null : profileData.gender,
      });

      // AuthContext의 user를 최신 프로필로 동기화 (재방문 시 stale 데이터 방지)
      const updated = response?.data;
      if (updated) {
        updateUser({
          name: updated.name,
          email: updated.email ?? null,
          phone_number: updated.phone_number ?? null,
          avatar_url: updated.avatar_url ?? null,
          birthday: updated.birthday ?? null,
          gender: updated.gender ?? null,
        });
      } else {
        // 200 OK지만 응답 body가 비어있는 경우. AuthContext는 stale로 남으므로
        // Header 등 다른 소비자가 구 값을 노출할 수 있다 — 관측을 위해 경고.
        logger.warn('[Profile] updateProfile succeeded without response data, AuthContext may be stale');
      }

      setOriginalProfileData({ ...profileData });
      setOriginalProfileImage(profileImage);
      setIsEditing(false);
      onBack();

      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        toast.success(profileTranslations[language].profileSaved, {
          duration: 1500,
          icon: '✓',
          style: SUCCESS_TOAST_STYLE,
        });
      }, 100);
    } catch (error) {
      logger.error('[Profile] Failed to save profile:', error);

      let errorMessage: string = ERROR_MESSAGES.default[language];

      if (error instanceof ApiClientError) {
        if (error.status === 401) {
          errorMessage = ERROR_MESSAGES.auth[language];
        } else if (error.code === 'NETWORK_ERROR' || error.code === 'TIMEOUT') {
          errorMessage = ERROR_MESSAGES.network[language];
        } else if (error.status === 422 || error.status === 400) {
          errorMessage = error.message;
        }
      }

      setSaveError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [profileData, profileImage, isSaving, updateUser]);

  const handleBackWithCheck = useCallback((onBack: () => void) => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onBack();
    }
  }, [hasUnsavedChanges]);

  const handleSaveAndExit = useCallback(async (onBack: () => void, language: Locale): Promise<void> => {
    setShowUnsavedChangesDialog(false);
    await handleSave(onBack, language);
  }, [handleSave]);

  const handleDiscardChanges = useCallback((onBack: () => void) => {
    setProfileData({ ...originalProfileData });
    setProfileImage(originalProfileImage);
    setShowUnsavedChangesDialog(false);
    onBack();
  }, [originalProfileData, originalProfileImage]);

  const handleBirthdayConfirm = useCallback(() => {
    if (!tempBirthday) return;
    setProfileData(prev => ({ ...prev, birthday: tempBirthday }));
    setIsBirthdayLocked(true);
    setShowBirthdayConfirm(false);
    setTempBirthday(null);
  }, [tempBirthday]);

  const handleBirthdayCancel = useCallback(() => {
    setShowBirthdayConfirm(false);
    setTempBirthday(null);
  }, []);

  return {
    isEditing,
    profileData,
    originalProfileData,
    profileImage,
    originalProfileImage,
    showAvatarModal,
    showCalendar,
    showBirthdayConfirm,
    tempBirthday,
    isBirthdayLocked,
    showUnsavedChangesDialog,
    fileInputRef,
    isSaving,
    saveError,

    setIsEditing,
    setProfileData,
    setProfileImage,
    setShowAvatarModal,
    setShowCalendar,
    setShowBirthdayConfirm,
    setTempBirthday,
    setIsBirthdayLocked,
    setShowUnsavedChangesDialog,

    hasUnsavedChanges,
    handleImageUpload,
    handleAvatarSelect,
    handleImageClick,
    handleSave,
    handleBackWithCheck,
    handleSaveAndExit,
    handleDiscardChanges,
    handleBirthdayConfirm,
    handleBirthdayCancel,
    clearSaveError,
  };
}
